/**
 * fctMotionVerbs — named, reusable FCT motion curves (spec §2.c).
 *
 * Each verb is a pure program `(t01, seed) → {dx, dy, alpha, scale}` over
 * normalized time; the executor (FloatingDamageText) owns the rAF loop,
 * label lifecycle, slot allocation, and setScale wiring. Programs are
 * deterministic under a fixed seed (the only seed use is arc's drift
 * direction, which follows the spawn-slot sign).
 *
 * Accessibility invariants (spec §3.4, locked by fctMotionVerbs.test.ts):
 *  - alpha is monotone non-increasing (no strobe / no yoyo flash),
 *  - alpha ends at 0,
 *  - hangThen* verbs hold alpha=1 through their hang window,
 *  - at most ONE scale overshoot (pop / hangThenPunch spawn stamp).
 *
 * Reduced-motion is handled UPSTREAM: the executor degrades any verb to the
 * legacy plain rise-fade when prefersReducedMotion() — programs here never
 * need to check it.
 */

import type { FctMotionVerb } from '../../../config/ui/fctStyle.config';

export interface VerbFrame {
  /** Lateral offset from spawn x (px). */
  dx: number;
  /** Vertical offset from spawn y (px, negative = up). */
  dy: number;
  /** 0..1. */
  alpha: number;
  /** Uniform label scale. */
  scale: number;
}

export type VerbProgram = (t01: number, seed: number) => VerbFrame;

const clamp01 = (t: number): number => (t < 0 ? 0 : t > 1 ? 1 : t);
const easeOutQuad = (t: number): number => 1 - (1 - t) * (1 - t);

/** Drift direction from the seed's sign (seed = slot offset; 0 → right). */
const dirOf = (seed: number): number => (seed < 0 ? -1 : 1);

/**
 * One-overshoot spawn stamp: scale rises `from → peak` over the first half
 * of `window`, settles `peak → 1` over the second half, then holds 1.
 */
function overshootScale(t: number, window: number, from: number, peak: number): number {
  if (t >= window) return 1;
  const half = window / 2;
  if (t < half) return from + (peak - from) * easeOutQuad(t / half);
  return peak + (1 - peak) * ((t - half) / half);
}

/** Linear fade from 1 → 0 starting at `fadeFrom`. */
function lateFade(t: number, fadeFrom: number): number {
  if (t <= fadeFrom) return 1;
  return 1 - (t - fadeFrom) / (1 - fadeFrom);
}

export const FCT_VERBS: Record<FctMotionVerb, VerbProgram> = {
  /** Bread-and-butter hit: 0.6→1.1→1.0 spawn overshoot, rise 40px, late fade. */
  pop: (t01) => {
    const t = clamp01(t01);
    return {
      dx: 0,
      dy: -40 * easeOutQuad(t),
      alpha: lateFade(t, 0.45),
      scale: overshootScale(t, 0.22, 0.6, 1.1),
    };
  },

  /** Heavy hit: rise with seeded lateral drift (±24px) + parabolic gravity tail. */
  arc: (t01, seed) => {
    const t = clamp01(t01);
    return {
      dx: dirOf(seed) * 24 * t,
      dy: -64 * easeOutQuad(t) + 20 * t * t,
      alpha: lateFade(t, 0.35),
      scale: 1,
    };
  },

  /** Chip damage / ticks: slow rise, early long fade, no scale motion. */
  drift: (t01) => {
    const t = clamp01(t01);
    return { dx: 0, dy: -40 * t, alpha: 1 - t, scale: 1 };
  },

  /** Big non-crit: fast rise 30px, hang at full alpha, then drop 12px fading. */
  hangThenFall: (t01) => {
    const t = clamp01(t01);
    const RISE = 0.15;
    const HANG_END = 0.55;
    if (t < RISE) {
      return { dx: 0, dy: -30 * easeOutQuad(t / RISE), alpha: 1, scale: 1 };
    }
    if (t < HANG_END) {
      return { dx: 0, dy: -30, alpha: 1, scale: 1 };
    }
    const p = (t - HANG_END) / (1 - HANG_END);
    return { dx: 0, dy: -30 + 12 * p * p, alpha: 1 - p, scale: 1 };
  },

  /** Crit stamp: 0.5→1.35→1.0 overshoot, quick rise, hang 60%, then fall. */
  hangThenPunch: (t01) => {
    const t = clamp01(t01);
    const STAMP = 0.18;
    const HANG_END = 0.6;
    const scale = overshootScale(t, STAMP, 0.5, 1.35);
    const rise = -24 * easeOutQuad(Math.min(t / STAMP, 1));
    if (t < HANG_END) {
      return { dx: 0, dy: rise, alpha: 1, scale };
    }
    const p = (t - HANG_END) / (1 - HANG_END);
    return { dx: 0, dy: -24 + 12 * p * p, alpha: 1 - p, scale };
  },

  /** Heal: drift base + one gentle 1.0→1.06 scale breathe. */
  softRise: (t01) => {
    const t = clamp01(t01);
    return {
      dx: 0,
      dy: -44 * t,
      alpha: 1 - t,
      scale: 1 + 0.06 * Math.sin(Math.PI * t),
    };
  },
};
